import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Wordmark } from "./Wordmark";
import { useUserAuth } from "../context/UserAuthContext";

const links = [
  { to: "/", label: "Home" },
  { to: "/#work", label: "Work" },
  { to: "/insights", label: "Insights" },
  { to: "/book", label: "Book" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signIn } = useUserAuth() || {};
  const SIGNIN_ENABLED = String(process.env.REACT_APP_ENABLE_SIGNIN || "false").toLowerCase() === "true";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location]);

  const handleHash = (e, to) => {
    if (to.startsWith("/#") && location.pathname === "/") {
      e.preventDefault();
      const el = document.getElementById(to.slice(2));
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled
          ? "bg-sand/80 backdrop-blur-xl border-b border-ink/10 py-4"
          : "bg-sand/40 backdrop-blur-md py-5"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="group">
          <Wordmark size="md" />
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.label}
              href={l.to}
              onClick={(e) => handleHash(e, l.to)}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="link-underline text-xs uppercase tracking-[0.2em] font-bold text-ink hover:text-clay transition-colors"
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/quiz"
            data-testid="nav-cta-button"
            className="text-xs uppercase tracking-[0.2em] font-bold bg-forest text-sand px-6 py-3 hover:bg-clay transition-colors duration-500"
          >
            Take the Quiz
          </Link>
          {SIGNIN_ENABLED && (user ? (
            <button
              data-testid="nav-user-avatar"
              onClick={() => navigate("/account")}
              className="flex items-center gap-2 group"
              aria-label="Account"
            >
              {user.picture ? (
                <img src={user.picture} alt={user.name || "Account"} className="w-9 h-9 rounded-full object-cover border border-ink/15 group-hover:border-teal transition-colors" />
              ) : (
                <span className="w-9 h-9 rounded-full bg-forest text-sand flex items-center justify-center font-serif text-sm">
                  {(user.name || user.email || "U").slice(0, 1).toUpperCase()}
                </span>
              )}
            </button>
          ) : (
            <button
              data-testid="nav-signin-button"
              onClick={signIn}
              className="text-xs uppercase tracking-[0.2em] font-bold text-ink hover:text-teal transition-colors"
            >
              Sign in
            </button>
          ))}
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden flex flex-col gap-1.5"
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
        >
          <span className="w-7 h-0.5 bg-ink" />
          <span className="w-7 h-0.5 bg-ink" />
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-sand border-t border-ink/10 px-6 py-6 flex flex-col gap-5">
          {links.map((l) => (
            <a
              key={l.label}
              href={l.to}
              onClick={(e) => handleHash(e, l.to)}
              className="text-sm uppercase tracking-[0.2em] font-bold text-ink"
            >
              {l.label}
            </a>
          ))}
          <Link to="/quiz" className="text-sm uppercase tracking-[0.2em] font-bold text-clay">
            Take the Quiz
          </Link>
        </div>
      )}
    </header>
  );
};
